import React, { useState } from "react";
import { Form } from "semantic-ui-react";
import { useFormik } from "formik";
import { Auth } from "../../../../api";
import { initialValues, validationSchema } from "./LoginForm.form";
import { AdminSuperuserSelect } from "./AdminSuperuserSelect";
import { AdminOtpInput } from "./AdminOtpInput";
import { AuthStepper } from "./AuthStepper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const authController = new Auth();

const STEP_LOGIN = 1;
const STEP_SELECT_SUPERADMIN = 2;
const STEP_ENTER_OTP = 3;

export function LoginForm() {
  const [step, setStep] = useState(STEP_LOGIN);
  // Tokens intermedios SOLO en memoria. Si se refresca la página, se pierden
  // a propósito y el usuario vuelve al paso 1.
  const [pendingAdminToken, setPendingAdminToken] = useState(null);
  const [otpToken, setOtpToken] = useState(null);
  const [otpEmail, setOtpEmail] = useState(null);

  const resetTo2FAStart = () => {
    setStep(STEP_LOGIN);
    setPendingAdminToken(null);
    setOtpToken(null);
    setOtpEmail(null);
  };

  const formik = useFormik({
    initialValues: initialValues(),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const response = await authController.login(formValue);
        // Rama 2FA admin: el backend pide segundo factor.
        if (response.requires2FA && response.pendingAdminToken) {
          setPendingAdminToken(response.pendingAdminToken);
          setStep(STEP_SELECT_SUPERADMIN);
          return;
        }
        // Si NO hay requires2FA, las credenciales son de un usuario NO admin.
        // El panel administrador solo admite admins (que SIEMPRE pasan por 2FA),
        // asi que rechazamos sin persistir tokens ni entrar. No-admin -> fuera.
        throw new Error("Esta cuenta no tiene permisos de administrador.");
      } catch (error) {
        toast.error(`Error: ${error.message}`, {
          position: "top-center",
          autoClose: false,
          closeOnClick: true,
        });
      }
    },
  });

  const renderStep = () => {
    if (step === STEP_SELECT_SUPERADMIN) {
      return (
        <AdminSuperuserSelect
          pendingAdminToken={pendingAdminToken}
          onOtpSent={({ otpToken: token, email }) => {
            setOtpToken(token);
            setOtpEmail(email);
            setStep(STEP_ENTER_OTP);
          }}
          onCancel={resetTo2FAStart}
        />
      );
    }
    if (step === STEP_ENTER_OTP) {
      return (
        <AdminOtpInput
          otpToken={otpToken}
          email={otpEmail}
          onBack={() => setStep(STEP_SELECT_SUPERADMIN)}
          onAbort={resetTo2FAStart}
        />
      );
    }
    return (
      <div className="auth-step">
        <h2 className="auth-step__title">Bienvenido</h2>
        <p className="auth-step__subtitle">
          Accede al panel administrador con tus credenciales. Si tu cuenta es de
          administrador, te pediremos un segundo factor de seguridad.
        </p>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Input
            type="text"
            icon="user"
            iconPosition="left"
            name="email"
            placeholder="Correo electrónico"
            onChange={formik.handleChange}
            value={formik.values.email}
            error={formik.errors.email}
          />
          <Form.Input
            type="password"
            icon="lock"
            iconPosition="left"
            name="password"
            placeholder="Contraseña"
            onChange={formik.handleChange}
            value={formik.values.password}
            error={formik.errors.password}
          />
          <Form.Button
            type="submit"
            primary
            fluid
            loading={formik.isSubmitting}
          >
            Entrar
          </Form.Button>
        </Form>
      </div>
    );
  };

  return (
    <>
      <ToastContainer />
      <AuthStepper currentStep={step} />
      {renderStep()}
    </>
  );
}
